import { useEffect, useState } from 'react';
import { Icon, Text, TextButton, Upsell } from '@zillow/constellation-10';
import { IconTourDoorFilled } from '@zillow/constellation-icons';
import { useQueryClient } from '@zg-rentals/react-query5';
import { ZCalendarWebApiHandler_getTouringTreatmentForZuidAndListing_Path } from '@zg-rentals/ts-z-calendar-client';

import TourAvailabilityWizard from '../TourAvailabilityWizard';
import useTourUpsellCookie from '../../hooks/useTourUpsellCookie';
import type { ClickStreamMetadata } from '../../constants/TouringClickStreamEvents';

type TourSchedulingUpsellProps = {
  listingAlias: string;
  modalHeaderText: string;
  onClick: () => void;
  clickStreamMetadata?: ClickStreamMetadata;
  eventId?: string;
  canAccessPostingPathTourDuration?: boolean;
  onWizardOpenChange?: (isOpen: boolean) => void;
};

const TourSchedulingUpsell = ({
  listingAlias,
  modalHeaderText,
  onClick,
  clickStreamMetadata,
  eventId,
  canAccessPostingPathTourDuration = false,
  onWizardOpenChange,
}: TourSchedulingUpsellProps) => {
  const queryClient = useQueryClient();

  const [displayTourAvailibilityWizard, setDisplayTourAvailibilityWizard] = useState(false);
  const { hasUpsellCookieSetForListing, setUpsellCookie } = useTourUpsellCookie(listingAlias);

  // Driven by state rather than the click/close handlers so the host is told the wizard is gone
  // even when this component unmounts underneath it.
  useEffect(() => {
    onWizardOpenChange?.(displayTourAvailibilityWizard);
    return () => onWizardOpenChange?.(false);
  }, [displayTourAvailibilityWizard, onWizardOpenChange]);

  const handleUpsellClick = () => {
    setDisplayTourAvailibilityWizard(true);
    onClick();
  };

  const handleUpsellClose = () => {
    setDisplayTourAvailibilityWizard(false);
    queryClient.invalidateQueries({
      predicate: (query) => query.queryKey[0] === ZCalendarWebApiHandler_getTouringTreatmentForZuidAndListing_Path,
    });
  };

  if (hasUpsellCookieSetForListing) return null;

  return (
    <>
      <Upsell
        header={<Text textStyle="body-lg-bold">Book tours easier, find renters faster</Text>}
        body={<Text>Set your tour availability now to save time.</Text>}
        media={<Icon size="xl" css={{ color: 'icon.accent.blue.hero' }} render={<IconTourDoorFilled />} />}
        action={
          <TextButton onClick={handleUpsellClick} data-eventid={eventId}>
            Set tour availability
          </TextButton>
        }
        elevated={false}
        tone="soft"
        onOpenChange={(open) => !open && setUpsellCookie()}
      />

      {displayTourAvailibilityWizard && (
        <TourAvailabilityWizard
          listingAlias={listingAlias}
          modalHeaderText={modalHeaderText}
          onClose={handleUpsellClose}
          clickStreamMetadata={clickStreamMetadata}
          canAccessPostingPathTourDuration={canAccessPostingPathTourDuration}
        />
      )}
    </>
  );
};

export default TourSchedulingUpsell;
